import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { collection } from 'firebase/firestore';
import { db } from '../firebase';
import { schoolMatches, docInScope, adminSchoolIds } from '../utils/schoolScope';
import { dataCacheService } from '../services/DataCacheService';
import StatCards from './StatCards';
import './AdminEnrollmentView.css';
import './AdminDetailPages.css';

const STATUSES = ['All', 'Active', 'Promoted', 'Retained', 'Transferred', 'Withdrawn', 'Graduated'];
const STATUS_COLORS = {
  Active: '#065f46', Promoted: '#166534', Retained: '#92400e',
  Transferred: '#5b21b6', Withdrawn: '#991b1b', Graduated: '#0e7490',
};

function AdminEnrollmentView({ adminData }) {
  const [records, setRecords] = useState([]);
  const [students, setStudents] = useState([]);
  const [classes, setClasses] = useState([]);
  const [academicYears, setAcademicYears] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('All');
  const [yearFilter, setYearFilter] = useState('All');
  const [searchText, setSearchText] = useState('');

  const schoolIds = useMemo(() => adminSchoolIds(adminData), [adminData]);

  const loadAll = useCallback(async () => {
    if (!adminData) return;
    setLoading(true);
    try {
      const ck = (p) => `${schoolIds.join('|') || 'all'}:${p}`;
      const [classDocs, yearDocs, enrollDocs] = await Promise.all([
        dataCacheService.getDocs(ck('classes'), collection(db, 'classes')),
        dataCacheService.getDocs(ck('academicYears'), collection(db, 'academicYears')),
        dataCacheService.getDocs(ck('enrollmentRecords'), collection(db, 'enrollmentRecords')),
      ]);

      const classesData = classDocs.filter(c => docInScope(c.schoolId, schoolIds));
      setClasses(classesData);

      setAcademicYears(yearDocs
        .filter(y => !y.schoolId || schoolIds.some(id => schoolMatches(y.schoolId, id)))
        .sort((a, b) => (b.startDate || '').localeCompare(a.startDate || '')));

      setRecords(enrollDocs.filter(r => docInScope(r.schoolId, schoolIds)));

      const perClass = await Promise.all(classesData.map(cls =>
        dataCacheService.getDocs(ck(`classes/${cls.id}/students`), collection(db, 'classes', cls.id, 'students'))
      ));
      const allStudents = perClass.flat().filter(s => s.isActive !== false);
      setStudents(allStudents);
      console.log('🔥 AdminEnrollment:', enrollDocs.length, 'records,', allStudents.length, 'students');
    } catch (e) {
      console.error('🔥 AdminEnrollment: load error', e);
    } finally {
      setLoading(false);
    }
  }, [adminData, schoolIds]);

  useEffect(() => { loadAll(); }, [loadAll]);

  // Real enrollment docs take precedence; when none exist, derive one record
  // per student from the roster (matches the iOS EnrollmentRecordsView fallback).
  const effectiveRecords = useMemo(() => {
    if (records.length > 0) return records;
    return students.map(s => {
      const cls = classes.find(c => c.id === s.classId);
      return {
        id: `derived-${s.id}`,
        studentId: s.id,
        studentName: s.fullName || `${s.firstName || ''} ${s.lastName || ''}`.trim(),
        studentExternalId: s.studentId || '',
        academicYearId: s.academicYearId || '',
        academicYearName: academicYears.find(y => y.id === s.academicYearId)?.name || '',
        gradeId: s.gradeId || '',
        gradeName: cls?.grade || '',
        classId: s.classId,
        className: cls?.name || '',
        status: s.currentEnrollmentStatus || 'Active',
        enrolledAt: s.createdAt || '',
      };
    });
  }, [records, students, classes, academicYears]);

  const filtered = effectiveRecords.filter(r => {
    if (statusFilter !== 'All' && String(r.status) !== statusFilter) return false;
    if (yearFilter !== 'All' && String(r.academicYearId) !== yearFilter) return false;
    if (searchText) {
      const q = searchText.toLowerCase();
      if (!(r.studentName || '').toLowerCase().includes(q) &&
          !(r.studentExternalId || '').toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const countFor = (status) => effectiveRecords.filter(r => String(r.status) === status).length;

  if (loading) {
    return <div className="loading-container"><div className="spinner"></div><p>Loading enrollment…</p></div>;
  }

  return (
    <div className="admin-enrollment-view">
      <div className="view-header"><h2>Enrollment Records</h2></div>

      <StatCards items={[
        ['Total', effectiveRecords.length],
        ['Active', countFor('Active')],
        ['Promoted', countFor('Promoted')],
        ['Retained', countFor('Retained')],
        ['Withdrawn', countFor('Withdrawn')],
        ['Graduated', countFor('Graduated')],
      ]} />

      {records.length === 0 && students.length > 0 && (
        <div className="info-banner">
          ℹ️ Showing enrollment derived from the student roster — formal enrollment records are created automatically when you run Promotions.
        </div>
      )}

      <div className="enrollment-filters">
        <input
          type="text"
          placeholder="Search by name or student ID…"
          value={searchText}
          onChange={e => setSearchText(e.target.value)}
        />
        <select value={yearFilter} onChange={e => setYearFilter(e.target.value)}>
          <option value="All">All Years</option>
          {academicYears.map(y => <option key={y.id} value={y.id}>{y.name}</option>)}
        </select>
      </div>

      <div className="status-chips">
        {STATUSES.map(s => (
          <button
            key={s}
            className={`status-chip ${statusFilter === s ? 'active' : ''}`}
            onClick={() => setStatusFilter(s)}
          >
            {s} {s !== 'All' && `(${countFor(s)})`}
          </button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <div className="empty-state"><p>No enrollment records match.</p></div>
      ) : (
        <div className="table-wrap">
        <table className="detail-table">
          <thead>
            <tr>
              <th>Student</th>
              <th>ID</th>
              <th>Class</th>
              <th>Grade</th>
              <th>Year</th>
              <th>Status</th>
              <th>Enrolled</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(r => (
              <tr key={r.id}>
                <td>{r.studentName}</td>
                <td>{r.studentExternalId || '—'}</td>
                <td>{r.className || '—'}</td>
                <td>{r.gradeName || '—'}</td>
                <td>{r.academicYearName || '—'}</td>
                <td>
                  <span className="status-badge" style={{
                    background: `${STATUS_COLORS[r.status] || '#374151'}18`,
                    color: STATUS_COLORS[r.status] || '#374151',
                  }}>{r.status}</span>
                </td>
                <td>{r.enrolledAt ? new Date(r.enrolledAt).toLocaleDateString() : '—'}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
}

export default AdminEnrollmentView;
